import React, { useState, useEffect } from "react";
import { Card, Table, Row, Col, Button, Form, Modal } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import Breadcrumb from "app/components/Breadcrumb";
import api from "app/services/api";

const formatAmount = (value) =>
  Number(value || 0).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

export default function StockSummary() {
  const [summaryData, setSummaryData] = useState({
    currentStock: 0,
    out_count: 0,
    logs_count: 0,
    in_count: 0,
  });
  const [productDetails, setProductDetails] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [showAll, setShowAll] = useState(true);
  const [showLogsModal, setShowLogsModal] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [stockLogs, setStockLogs] = useState([]);
  const [logsLoading, setLogsLoading] = useState(false);
  const [movementTypeFilter, setMovementTypeFilter] = useState(null); // null = all, 'IN', 'OUT'
  const [outwardTypeFilter, setOutwardTypeFilter] = useState("All"); // All, company_use, po_wise, dispatch

  useEffect(() => {
    fetchStockSummary();
  }, []);

  const fetchStockSummary = () => {
    setLoading(true);
    api
      .get("/api/stock/summary", {
        withCredentials: true,
      })
      .then((res) => {
        setSummaryData(res.data.summary || {
          currentStock: 0,
          out_count: 0,
          logs_count: 0,
          in_count: 0,
        });
        setProductDetails(res.data.productDetails || []);
        setLoading(false);
      })
      .catch((err) => {
        setLoading(false);
      });
  };

  const handleViewLogs = (product) => {
    setSelectedProduct(product);
    setLogsLoading(true);
    setShowLogsModal(true);
    setMovementTypeFilter(null);
    setOutwardTypeFilter("All");

    api
      .get(`/api/stock/logs?productId=${encodeURIComponent(product.product_id)}`, {
        withCredentials: true,
      })
      .then((res) => {
        setStockLogs(res.data.logs || []);
        setLogsLoading(false);
      })
      .catch((err) => {
        setStockLogs([]);
        setLogsLoading(false);
      });
  };

  // SEARCH FILTER
  const filteredProductDetails = productDetails.filter((item) =>
    (item.product_name && item.product_name.toLowerCase().includes(search.toLowerCase())) ||
    (item.category_name && item.category_name.toLowerCase().includes(search.toLowerCase()))
  );

  // PAGINATION
  const totalRows = filteredProductDetails.length;
  const totalPages =
    rowsPerPage >= totalRows ? 1 : Math.ceil(totalRows / rowsPerPage);

  const startIndex = (currentPage - 1) * rowsPerPage;

  const paginatedProductDetails = showAll
    ? filteredProductDetails
    : filteredProductDetails.slice(startIndex, startIndex + rowsPerPage);

  const grandTotalValue = filteredProductDetails.reduce(
    (sum, item) => sum + Number(item.total_value || 0),
    0,
  );

  const exportToExcel = () => {
    const escapeCsvValue = (value) => {
      const text = String(value ?? "");
      const safeText = /^[\s]*[=+\-@]/.test(text) ? `'${text}` : text;
      return `"${safeText.replace(/"/g, '""')}"`;
    };
    const rows = [
      ["#", "Product", "Category Name", "Unit", "Total Qty", "Stock Price", "Total Value"],
      ...filteredProductDetails.map((product, index) => [
        index + 1,
        product.product_name || product.product_description || "",
        product.category_name || "",
        product.unit || "",
        Number(product.current_stock || 0),
        Number(product.stock_price || 0),
        Number(product.total_value || 0),
      ]),
      ["", "", "", "", "", "Total", grandTotalValue],
    ];
    const csv = rows.map((row) => row.map(escapeCsvValue).join(",")).join("\r\n");
    const blob = new Blob(["\uFEFF", csv], { type: "text/csv;charset=utf-8;" });
    const downloadUrl = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = downloadUrl;
    link.download = "product-stock-summary.csv";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(downloadUrl);
  };

  const StatCard = ({ title, value, onClick, clickable }) => (
    <Card
      className="h-100"
      style={{
        cursor: clickable ? 'pointer' : 'default',
        transition: clickable ? 'transform 0.2s, box-shadow 0.2s' : 'none',
      }}
      onClick={onClick}
      onMouseEnter={clickable ? (e) => {
        e.currentTarget.style.transform = 'translateY(-2px)';
        e.currentTarget.style.boxShadow = '0 4px 8px rgba(0,0,0,0.1)';
      } : null}
      onMouseLeave={clickable ? (e) => {
        e.currentTarget.style.transform = 'translateY(0)';
        e.currentTarget.style.boxShadow = 'none';
      } : null}
    >
      <Card.Body>
        <Card.Title className="text-muted mb-2" style={{ fontSize: '14px' }}>
          {title}
        </Card.Title>
        <Card.Text style={{ fontSize: '28px', fontWeight: 'bold', color: '#6f42c1' }}>
          {value}
        </Card.Text>
      </Card.Body>
    </Card>
  );

  // Filtered logs based on buttons and dropdown selection in modal
  const filteredLogs = stockLogs.filter((log) => {
    // Check movement type filter
    if (movementTypeFilter && String(log.movement_type) !== movementTypeFilter) return false;

    // Check outward type filter
    if (outwardTypeFilter !== "All") {
      return String(log.outward_type || "").toLowerCase() === String(outwardTypeFilter).toLowerCase();
    }

    return true;
  });

  const filteredTotal = filteredLogs.reduce((sum, log) => sum + (log.movement_type === "IN" ? Number(log.qty || 0) : -Number(log.qty || 0)), 0);

  return (
    <div>
      <Breadcrumb
        routeSegments={[
          { name: "Dashboard", path: "/" },
          { name: "Product Management", path: "/product" },
          { name: "Stock" },
        ]}
      />

      <Card body>
        <div className="d-flex justify-content-between align-items-center mb-2">
          <Card.Title>Product Stock Details</Card.Title>

          {/* SEARCH RIGHT */}
          <div className="d-flex align-items-center">
            {/* SEARCH INPUT */}
            <input
              type="text"
              placeholder="Search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{
                width: "260px",
                height: "38px",
                border: "1px solid #d1d1d1",
                borderRight: "none",
                paddingLeft: "10px",
                outline: "none",
              }}
            />

            {/* SEARCH ICON */}
            <div
              style={{
                width: "40px",
                height: "38px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                border: "1px solid #d1d1d1",
                borderLeft: "none",
                background: "#f5f5f5",
              }}
            >
              <i className="i-Magnifi-Glass"></i>
            </div>

            <Button
              onClick={exportToExcel}
              title="Export stock to Excel"
              aria-label="Export stock to Excel"
              className="d-flex align-items-center gap-1"
              style={{
                height: "38px",
                backgroundColor: "#198754",
                border: "none",
                borderTopLeftRadius: 0,
                borderBottomLeftRadius: 0,
              }}
            >
              <i className="i-File-CSV"></i>
              Excel
            </Button>
          </div>
        </div>

        <Table bordered hover style={{ marginTop: "0" }}>
          <thead>
            <tr>
              <th style={{ backgroundColor: "#cfc7d9" }}>#</th>
              <th style={{ backgroundColor: "#cfc7d9" }}>Product</th>
              <th style={{ backgroundColor: "#cfc7d9" }}>Category Name</th>
              <th style={{ backgroundColor: "#cfc7d9" }}>Unit</th>
              <th style={{ backgroundColor: "#cfc7d9" }}>Total Qty</th>
              <th style={{ backgroundColor: "#cfc7d9" }}>Stock Price</th>
              <th style={{ backgroundColor: "#cfc7d9" }}>Total Value</th>

            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan="7" className="text-center">
                  Loading...
                </td>
              </tr>
            ) : paginatedProductDetails.length === 0 ? (
              <tr>
                <td colSpan="7" className="text-center">
                  No products found
                </td>
              </tr>
            ) : (
              paginatedProductDetails.map((product, index) => (
                <tr key={index}>
                  <td>{startIndex + index + 1}</td>
                  <td>
                    <div>

                      <div style={{ fontWeight: 'bold', fontSize: '14px' }}>
                        {product.product_name || product.product_description || "-"}
                      </div>

                    </div>
                  </td>
                  <td>{product.category_name || "-"}</td>
                  <td>{product.unit || "-"}</td>
                  <td>{product.current_stock || 0}</td>
                  <td>{product.stock_price != null ? `₹${formatAmount(product.stock_price)}` : "-"}</td>
                  <td>₹{formatAmount(product.total_value)}</td>
                </tr>
              ))
            )}
          </tbody>
          {!loading && filteredProductDetails.length > 0 && (
            <tfoot>
              <tr>
                <td colSpan="6" className="text-end fw-bold">Total:</td>
                <td className="fw-bold">{formatAmount(grandTotalValue)}</td>
              </tr>
            </tfoot>
          )}
        </Table>

        <div className="d-flex justify-content-end align-items-center mt-3">
          <span className="me-2">Rows per page</span>

          <Form.Select
            value={showAll ? "all" : rowsPerPage}
            onChange={(e) => {
              if (e.target.value === "all") {
                setShowAll(true);
                setRowsPerPage(filteredProductDetails.length);
              } else {
                setShowAll(false);
                setRowsPerPage(Number(e.target.value));
              }
              setCurrentPage(1);
            }}
            style={{ width: "90px", marginRight: "15px" }}
          >
            <option value={5}>5</option>
            <option value={20}>20</option>
            <option value="all">All</option>
          </Form.Select>
          <span className="me-3">
            {startIndex + 1}-{Math.min(startIndex + rowsPerPage, totalRows)} of{" "}
            {totalRows}
          </span>

          {/* FIRST */}
          <Button
            style={{
              backgroundColor: "#7d5bbe",
              border: "none",
              marginRight: "5px",
            }}
            disabled={currentPage === 1}
            onClick={() => setCurrentPage(1)}
          >
            ⏮
          </Button>

          {/* PREVIOUS */}
          <Button
            style={{
              backgroundColor: "#7d5bbe",
              border: "none",
              marginRight: "5px",
            }}
            disabled={currentPage === 1}
            onClick={() => setCurrentPage(currentPage - 1)}
          >
            ◀
          </Button>

          {/* NEXT */}
          <Button
            style={{
              backgroundColor: "#7d5bbe",
              border: "none",
              marginRight: "5px",
            }}
            disabled={currentPage === totalPages}
            onClick={() => setCurrentPage(currentPage + 1)}
          >
            ▶
          </Button>

          {/* LAST */}
          <Button
            style={{ backgroundColor: "#7d5bbe", border: "none" }}
            disabled={currentPage === totalPages}
            onClick={() => setCurrentPage(totalPages)}
          >
            ⏭
          </Button>
        </div>
      </Card>

      {/* Stock Logs Modal */}
      <Modal show={showLogsModal} onHide={() => { setShowLogsModal(false); setMovementTypeFilter(null); setOutwardTypeFilter("All"); }} size="lg">
        <Modal.Header closeButton style={{ backgroundColor: "#cfc7d9" }}>
          <Modal.Title>
            Stock Movement Logs - {selectedProduct?.product_name || ""}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {logsLoading ? (
            <div className="text-center">Loading logs...</div>
          ) : stockLogs.length === 0 ? (
            <div className="text-center">No stock movement logs found</div>
          ) : (
            <>
              <div className="mb-3 d-flex justify-content-between align-items-center">
                <div>
                  <strong>Stock calculated from movement logs:</strong>{" "}
                  {filteredTotal}
                </div>
                <div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
                  <Button
                    size="sm"
                    variant={movementTypeFilter === "IN" ? "primary" : "outline-secondary"}
                    onClick={() => setMovementTypeFilter(movementTypeFilter === "IN" ? null : "IN")}
                  >
                    IN
                  </Button>
                  <Button
                    size="sm"
                    variant={movementTypeFilter === "OUT" ? "primary" : "outline-secondary"}
                    onClick={() => setMovementTypeFilter(movementTypeFilter === "OUT" ? null : "OUT")}
                  >
                    OUT
                  </Button>
                  <Form.Select size="sm" value={outwardTypeFilter} onChange={(e) => setOutwardTypeFilter(e.target.value)} style={{ width: 150 }}>
                    <option value="All">All Types</option>
                    <option value="company_use">Company Use</option>
                    <option value="po_wise">Part Wise</option>
                    <option value="dispatch">Dispatch</option>
                  </Form.Select>
                </div>
              </div>
              {/* apply filter */}
              {(() => { })()}

              {/* derive filtered logs */}




              <Table bordered hover>
                <thead>
                  <tr>
                    <th style={{ backgroundColor: "#cfc7d9" }}>#</th>
                    <th style={{ backgroundColor: "#cfc7d9" }}>Date / Time</th>
                    <th style={{ backgroundColor: "#cfc7d9" }}>Mode</th>
                    <th style={{ backgroundColor: "#cfc7d9" }}>Type</th>
                    <th style={{ backgroundColor: "#cfc7d9" }}>Product</th>
                    <th style={{ backgroundColor: "#cfc7d9" }}>Supplier</th>
                    <th style={{ backgroundColor: "#cfc7d9" }}>In Qty</th>
                    <th style={{ backgroundColor: "#cfc7d9" }}>Out Qty</th>
                    <th style={{ backgroundColor: "#cfc7d9" }}>Created By</th>
                    <th style={{ backgroundColor: "#cfc7d9" }}>Updated By</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredLogs.map((log, index) => (
                    <tr key={`${log.movement_type}-${log.id}-${index}`}>
                      <td>{index + 1}</td>
                      <td>{log.date ? new Date(log.date).toLocaleString() : "-"}</td>
                      <td>
                        <span
                          style={{
                            backgroundColor: log.movement_type === "IN" ? "#d4edda" : "#f8d7da",
                            color: log.movement_type === "IN" ? "#155724" : "#721c24",
                            padding: "3px 10px",
                            borderRadius: "4px",
                            fontWeight: "bold",
                          }}
                        >
                          {log.movement_type}
                        </span>
                      </td>
                      <td>{log.outward_type ? (log.outward_type === "company_use" ? "Company Use" : (log.outward_type === "po_wise" ? "Part Wise" : (log.outward_type === "dispatch" ? "Dispatch" : log.outward_type))) : "-"}</td>
                      <td>{log.product_name || "-"}</td>
                      <td>{log.supplier_name || "-"}</td>
                      <td>{log.movement_type === "IN" ? log.qty || "-" : "-"}</td>
                      <td>{log.movement_type === "OUT" ? log.qty || "-" : "-"}</td>
                      <td>{log.created_by_name || "-"}</td>
                      <td>{log.updated_by_name || "-"}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowLogsModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}
